import { getDocument } from 'pdfjs-dist/legacy/build/pdf.mjs'
import { WorkerMessageHandler } from 'pdfjs-dist/legacy/build/pdf.worker.mjs'

declare global {
  // pdfjs usa este global para evitar carregar worker externo
  var pdfjsWorker: { WorkerMessageHandler: typeof WorkerMessageHandler } | undefined
}

function configurePdfWorker() {
  globalThis.pdfjsWorker = { WorkerMessageHandler }
}

interface PdfTextItem {
  str: string
  transform: number[]
}

export interface MatriculaTurnos {
  m: number
  t: number
  n: number
  i: number
  total: number
}

export interface EscolaR065Parsed {
  codigo: string
  nome: string
  tipo: 'EMEF' | 'EMEI' | 'PROJETO'
  infantil: MatriculaTurnos
  fundMenor: MatriculaTurnos
  fundMaior: MatriculaTurnos
  eja: MatriculaTurnos
  totalEscola: number
}

export interface PdfItem {
  page: number
  x: number
  y: number
  str: string
}

const MODALITY_HEADERS = [
  { key: 'infantil' as const, label: 'INFANTIL' },
  { key: 'fundMenor' as const, label: 'FUNDAMENTAL MENOR' },
  { key: 'fundMaior' as const, label: 'FUNDAMENTAL MAIOR' },
  { key: 'eja' as const, label: 'EJA' },
]

const TURN_TYPES = ['M', 'T', 'N', 'I', 'TOTAL'] as const
type TurnType = (typeof TURN_TYPES)[number]

function emptyTurnos(): MatriculaTurnos {
  return { m: 0, t: 0, n: 0, i: 0, total: 0 }
}

function sumTurnos(t: MatriculaTurnos) {
  return t.m + t.t + t.n + t.i
}

async function extractItems(file: File): Promise<PdfItem[]> {
  const data = new Uint8Array(await file.arrayBuffer())
  const doc = await getDocument({ data, useSystemFonts: true }).promise
  const items: PdfItem[] = []

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum)
    const content = await page.getTextContent()
    for (const raw of content.items) {
      const item = raw as PdfTextItem
      const str = item.str?.trim()
      if (!str) continue
      items.push({
        page: pageNum,
        x: item.transform[4],
        y: item.transform[5],
        str,
      })
    }
  }
  return items
}

function numAt(items: PdfItem[], page: number, x: number, y: number, tolX = 8, tolY = 10) {
  const hits = items.filter(
    (i) =>
      i.page === page &&
      /^\d[\d.]*$/.test(i.str) &&
      Math.abs(i.x - x) <= tolX &&
      Math.abs(i.y - y) <= tolY,
  )
  if (!hits.length) return 0
  return parseInt(hits[0].str.replace(/\./g, ''), 10)
}

function turnLabelsOnPage(items: PdfItem[], page: number) {
  return items.filter(
    (i) => i.page === page && (TURN_TYPES as readonly string[]).includes(i.str) && i.x > 100 && i.x < 118,
  )
}

function modalityHeadersOnPage(items: PdfItem[], page: number) {
  const labels = new Set(MODALITY_HEADERS.map((h) => h.label))
  return items
    .filter((i) => i.page === page && labels.has(i.str) && i.x < 95)
    .sort((a, b) => a.y - b.y)
}

function labelsByTypeSorted(rowLabels: PdfItem[]) {
  const byType: Partial<Record<TurnType, PdfItem[]>> = {}
  for (const turn of TURN_TYPES) {
    byType[turn] = rowLabels.filter((r) => r.str === turn).sort((a, b) => a.y - b.y)
  }
  return byType
}

/** Cada modalidade recebe o i-ésimo rótulo M/T/N/I (ordenado por Y). */
function modalitySections(items: PdfItem[], page: number) {
  const headers = modalityHeadersOnPage(items, page)
  const rowLabels = turnLabelsOnPage(items, page)
  const byType = labelsByTypeSorted(rowLabels)

  return MODALITY_HEADERS.map((def, idx) => {
    const header = headers.find((h) => h.str === def.label)
    const rows: Partial<Record<TurnType, number>> = {}
    for (const turn of TURN_TYPES) {
      const pick = byType[turn]?.[idx]
      if (pick) rows[turn] = pick.y
    }
    return { key: def.key, label: def.label, headerY: header?.y ?? null, rows }
  })
}

function readTurnosBlock(items: PdfItem[], page: number, schoolX: number, rows: Partial<Record<TurnType, number>>) {
  const block: MatriculaTurnos = {
    m: rows.M ? numAt(items, page, schoolX, rows.M) : 0,
    t: rows.T ? numAt(items, page, schoolX, rows.T) : 0,
    n: rows.N ? numAt(items, page, schoolX, rows.N) : 0,
    i: rows.I ? numAt(items, page, schoolX, rows.I) : 0,
    total: 0,
  }
  block.total = sumTurnos(block)
  return block
}

function pageSchools(items: PdfItem[], page: number) {
  const out: { tipo: EscolaR065Parsed['tipo']; nome: string; codigo: string; x: number }[] = []
  for (const item of items.filter((i) => i.page === page)) {
    const m = item.str.match(/^(EMEF|EMEI|PROJETO)\s+(.+?)\s*-\s*ID:\s*(\d+)\s*$/i)
    if (!m) continue
    out.push({
      tipo: m[1].toUpperCase() as EscolaR065Parsed['tipo'],
      nome: `${m[1].toUpperCase()} ${m[2].trim()}`,
      codigo: m[3],
      x: item.x,
    })
  }
  return out
}

export function parsePage(items: PdfItem[], page: number): EscolaR065Parsed[] {
  const sections = modalitySections(items, page)

  return pageSchools(items, page).map((school) => {
    const result: EscolaR065Parsed = {
      codigo: school.codigo,
      nome: school.nome,
      tipo: school.tipo,
      infantil: emptyTurnos(),
      fundMenor: emptyTurnos(),
      fundMaior: emptyTurnos(),
      eja: emptyTurnos(),
      totalEscola: 0,
    }

    for (const sec of sections) {
      if (!sec.headerY) continue
      result[sec.key] = readTurnosBlock(items, page, school.x, sec.rows)
    }

    result.totalEscola =
      result.infantil.total + result.fundMenor.total + result.fundMaior.total + result.eja.total

    return result
  })
}

/** Páginas além desta são ignoradas: o R065 publica as escolas nas primeiras. */
export const MAX_PAGINAS_R065 = 4

/** Núcleo puro da extração, sem pdfjs — espelhado em scripts/parseR065Core.mjs. */
export function parseAllPages(items: PdfItem[]): EscolaR065Parsed[] {
  const pages = [...new Set(items.map((i) => i.page))]
    .filter((p) => p <= MAX_PAGINAS_R065)
    .sort((a, b) => a - b)
  const all: EscolaR065Parsed[] = []
  for (const page of pages) {
    if (pageSchools(items, page).length) all.push(...parsePage(items, page))
  }
  return all
}

export async function parseR065Pdf(file: File): Promise<EscolaR065Parsed[]> {
  configurePdfWorker()
  return parseAllPages(await extractItems(file))
}

export function formatTurnos(t: MatriculaTurnos) {
  return `M:${t.m} T:${t.t} N:${t.n} I:${t.i} Σ:${t.total}`
}
